import React, { useMemo, useState } from 'react';
import { Alert, Button, Input, Table, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { InboxOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import type { TransportSupplierOrderSummaryDto } from '../dto/transport';
import SupplierOrderProductsPreview from '../modules/orders/components/SupplierOrderProductsPreview';
import SupplierOrderSummaryPreview, {
  getSupplierOrderSummary,
} from '../modules/orders/components/SupplierOrderSummaryPreview';
import './TransportSupplierOrdersField.css';

/**
 * The inbound half of "what is on this truck".
 *
 * The picker mirrors the supplier-order list: the operator sees the supplier,
 * canonical ZD number, status and every product before selecting a row. The
 * component owns only filtering and selection presentation; transport payload
 * state remains in SharedTransportForm.
 */
interface TransportSupplierOrdersFieldProps {
  availableSupplierOrders: SupplierOrderDto[];
  selectedSupplierOrders?: TransportSupplierOrderSummaryDto[];
  value: string[];
  onChange: (supplierOrderIds: string[]) => void;
  loading?: boolean;
  loadError?: boolean;
  onRetry?: () => void;
}

const TransportSupplierOrdersField: React.FC<TransportSupplierOrdersFieldProps> = ({
  availableSupplierOrders,
  selectedSupplierOrders = [],
  value,
  onChange,
  loading = false,
  loadError = false,
  onRetry,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const [search, setSearch] = useState('');

  const supplierOrderRows = useMemo<Array<SupplierOrderDto | TransportSupplierOrderSummaryDto>>(() => {
    const rows = new Map<string, SupplierOrderDto | TransportSupplierOrderSummaryDto>(
      availableSupplierOrders.map((supplierOrder) => [supplierOrder.id, supplierOrder]),
    );

    selectedSupplierOrders.forEach((supplierOrder) => {
      if (value.includes(supplierOrder.id) && !rows.has(supplierOrder.id)) {
        rows.set(supplierOrder.id, supplierOrder);
      }
    });

    return Array.from(rows.values());
  }, [availableSupplierOrders, selectedSupplierOrders, value]);

  const filteredSupplierOrders = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('pl');
    if (!query) return supplierOrderRows;

    return supplierOrderRows.filter((supplierOrder) => {
      const summary = getSupplierOrderSummary(supplierOrder);
      const productText = ('items' in supplierOrder ? supplierOrder.items || [] : [])
        .map((item) => [item.name, item.description].filter(Boolean).join(' '))
        .join(' ');
      const searchText = [
        summary.displayNumber,
        supplierOrder.orderNumber,
        'manualOrderNumber' in supplierOrder ? supplierOrder.manualOrderNumber : undefined,
        summary.supplierName,
        'status' in supplierOrder ? supplierOrder.status : undefined,
        productText,
      ].filter(Boolean).join(' ').toLocaleLowerCase('pl');

      return searchText.includes(query);
    });
  }, [search, supplierOrderRows]);

  const columns = useMemo<ColumnsType<SupplierOrderDto | TransportSupplierOrderSummaryDto>>(
    () => [
      {
        title: t('transport:supplierOrders.orderColumn'),
        key: 'summary',
        width: '36%',
        onCell: () => ({
          'data-label': t('transport:supplierOrders.orderColumn'),
        } as React.TdHTMLAttributes<HTMLTableCellElement>),
        render: (_, supplierOrder) => (
          <SupplierOrderSummaryPreview order={supplierOrder} />
        ),
      },
      {
        title: t('transport:supplierOrders.productsColumn'),
        key: 'products',
        onCell: () => ({
          'data-label': t('transport:supplierOrders.productsColumn'),
        } as React.TdHTMLAttributes<HTMLTableCellElement>),
        render: (_, supplierOrder) => (
          <SupplierOrderProductsPreview
            items={'items' in supplierOrder ? supplierOrder.items : undefined}
          />
        ),
      },
    ],
    [t],
  );

  const rowSelection = useMemo<TableProps<SupplierOrderDto | TransportSupplierOrderSummaryDto>['rowSelection']>(() => ({
    selectedRowKeys: value,
    preserveSelectedRowKeys: true,
    columnWidth: 42,
    onChange: (keys) => onChange(keys.map(String)),
    getCheckboxProps: (supplierOrder) => {
      const summary = getSupplierOrderSummary(supplierOrder);
      return {
        name: `supplier-order-${supplierOrder.id}`,
        'aria-label': t('transport:supplierOrders.selectOrder', {
          number: summary.displayNumber,
        }),
      };
    },
    getTitleCheckboxProps: () => ({
      'aria-label': t('transport:supplierOrders.selectAll'),
    }),
  }), [onChange, t, value]);

  return (
    <section
      className="transport-supplier-orders"
      data-testid="transport-supplier-orders-field"
      aria-label={t('transport:supplierOrders.title')}
    >
      <header className="transport-supplier-orders__header">
        <span className="transport-supplier-orders__icon" aria-hidden="true"><InboxOutlined /></span>
        <div className="transport-supplier-orders__heading">
          <strong>{t('transport:supplierOrders.title')}</strong>
          <small>{t('transport:supplierOrders.hint')}</small>
        </div>
      </header>

      {loadError ? (
        <Alert
          type="error"
          showIcon
          message={t('transport:errorFetchingOrders')}
          action={onRetry ? (
            <Button type="link" size="small" onClick={onRetry} disabled={loading}>
              {t('common:retry')}
            </Button>
          ) : null}
        />
      ) : null}

      <div className="transport-supplier-orders__toolbar">
        <Input
          allowClear
          className="transport-supplier-orders__search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          prefix={<SearchOutlined aria-hidden="true" />}
          placeholder={t('transport:supplierOrders.searchPlaceholder')}
          aria-label={t('transport:supplierOrders.searchPlaceholder')}
          data-testid="transport-supplier-orders-search"
        />
        <div className="transport-supplier-orders__selection-summary" aria-live="polite">
          <Typography.Text type={value.length > 0 ? undefined : 'secondary'}>
            {t('transport:supplierOrders.selectedCount', { count: value.length })}
          </Typography.Text>
          {value.length > 0 ? (
            <Button type="link" size="small" onClick={() => onChange([])}>
              {t('common:clear')}
            </Button>
          ) : null}
        </div>
      </div>

      <Table<SupplierOrderDto | TransportSupplierOrderSummaryDto>
        className="transport-supplier-orders__table"
        data-testid="transport-supplier-orders-table"
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={filteredSupplierOrders}
        rowSelection={rowSelection}
        pagination={{ pageSize: 6, hideOnSinglePage: true }}
        locale={{ emptyText: t('transport:supplierOrders.empty') }}
        onRow={(supplierOrder) => ({
          onClick: (event) => {
            const target = event.target as HTMLElement;
            if (target.closest('button, a, input, label, .ant-checkbox-wrapper')) return;
            onChange(
              value.includes(supplierOrder.id)
                ? value.filter((id) => id !== supplierOrder.id)
                : [...value, supplierOrder.id],
            );
          },
        })}
      />
    </section>
  );
};

export default TransportSupplierOrdersField;
